import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminUserView } from '@claude-code-api/shared';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Coins, TrendingUp, Zap, ChevronLeft, ChevronRight } from 'lucide-react';

function formatUSD(usd: number): string {
  return `$${usd < 0.01 && usd > 0 ? usd.toFixed(4) : usd.toFixed(2)}`;
}

function formatHKD(hkd: number): string {
  return `HK$${hkd < 0.01 && hkd > 0 ? hkd.toFixed(4) : hkd.toFixed(2)}`;
}

interface PlatformSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCacheReadTokens: number;
  totalCacheCreationTokens: number;
  totalCreditsDeducted: number;
  totalCostUsd: number;
  totalCostHkd: number;
  totalRequests: number;
  totalCreditsGranted: number;
  totalCreditBalance: number;
}

interface UsageRecord {
  id: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  cacheCreationTokens?: number;
  totalTokens: number;
  creditsDeducted: number;
  costUsd: number;
  costHkd: number;
  requestPath: string | null;
  createdAt: string;
  user?: { name: string | null; email: string };
}

export default function AdminCreditsPage() {
  const { t, i18n } = useTranslation(['adminCredits', 'common']);
  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [loading, setLoading] = useState(true);

  // Credits modal
  const [selectedUser, setSelectedUser] = useState<AdminUserView | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState(0);
  const [creditsOperation, setCreditsOperation] = useState<'set' | 'add'>('add');

  // User usage detail modal
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [usageRecords, setUsageRecords] = useState<UsageRecord[]>([]);
  const [usagePage, setUsagePage] = useState(1);
  const [usageTotalPages, setUsageTotalPages] = useState(1);
  const [usageLoading, setUsageLoading] = useState(false);

  // Platform-wide usage history
  const [allUsageRecords, setAllUsageRecords] = useState<UsageRecord[]>([]);
  const [allUsagePage, setAllUsagePage] = useState(1);
  const [allUsageTotalPages, setAllUsageTotalPages] = useState(1);
  const [allUsageLoading, setAllUsageLoading] = useState(false);

  useEffect(() => {
    fetchData();
    fetchAllUsage(1);
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, usersRes] = await Promise.all([
        fetch('/api/admin/usage/summary', { credentials: 'include' }),
        fetch('/api/admin/users?limit=100', { credentials: 'include' }),
      ]);

      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data.data);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch credit data:', error);
      toast.error(t('adminCredits:errors.loadCreditData'));
    } finally {
      setLoading(false);
    }
  };

  const updateCredits = async () => {
    if (!selectedUser) return;

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}/credits`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ amount: creditsAmount, operation: creditsOperation }),
      });

      if (response.ok) {
        toast.success(t('adminCredits:messages.creditsUpdated'));
        setShowCreditsModal(false);
        setSelectedUser(null);
        fetchData();
      } else {
        const data = await response.json();
        toast.error(data.error || t('adminCredits:errors.updateCredits'));
      }
    } catch (error) {
      console.error('Failed to update credits:', error);
      toast.error(t('adminCredits:errors.updateCredits'));
    }
  };

  const fetchUserUsage = async (userId: string, page: number) => {
    setUsageLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${userId}/usage?page=${page}&limit=20`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsageRecords(data.data.records);
        setUsageTotalPages(data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch user usage:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchAllUsage = async (page: number) => {
    setAllUsageLoading(true);
    try {
      const res = await fetch(`/api/admin/usage/history?page=${page}&limit=20`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllUsageRecords(data.data.records);
        setAllUsageTotalPages(data.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch all usage:', error);
    } finally {
      setAllUsageLoading(false);
    }
  };

  const openUsageModal = (user: AdminUserView) => {
    setSelectedUser(user);
    setUsagePage(1);
    setShowUsageModal(true);
    fetchUserUsage(user.id, 1);
  };

  useEffect(() => {
    if (showUsageModal && selectedUser) {
      fetchUserUsage(selectedUser.id, usagePage);
    }
  }, [usagePage]);

  useEffect(() => {
    fetchAllUsage(allUsagePage);
  }, [allUsagePage]);

  // Sort users by credits used descending
  const sortedUsers = [...users].sort((a, b) => Number(b.totalCreditsUsed) - Number(a.totalCreditsUsed));
  const formatDateTime = (value: string) => new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('adminCredits:title')} description={t('adminCredits:description')} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-lg" />)}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t('adminCredits:title')} description={t('adminCredits:description')} />

      {/* Platform Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.totalGranted')}</span>
            </div>
            <p className="text-xl font-bold mt-1">{summary.totalCreditsGranted.toLocaleString()}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.totalUsed')}</span>
            </div>
            <p className="text-xl font-bold mt-1">{summary.totalCreditsDeducted.toLocaleString()}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Coins className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.totalBalance')}</span>
            </div>
            <p className="text-xl font-bold mt-1">{summary.totalCreditBalance.toLocaleString()}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.totalRequests')}</span>
            </div>
            <p className="text-xl font-bold mt-1">{summary.totalRequests.toLocaleString()}</p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.totalTokens')}</span>
            </div>
            <p className="text-lg font-bold mt-1">
              {(
                summary.totalInputTokens
                + summary.totalOutputTokens
                + summary.totalCacheReadTokens
                + summary.totalCacheCreationTokens
              ).toLocaleString()}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('adminCredits:summary.inputOutput', {
                input: summary.totalInputTokens.toLocaleString(),
                output: summary.totalOutputTokens.toLocaleString(),
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('adminCredits:summary.cacheReadCreate', {
                read: summary.totalCacheReadTokens.toLocaleString(),
                create: summary.totalCacheCreationTokens.toLocaleString(),
              })}
            </p>
          </div>
          <div className="bg-surface-0 border rounded-lg p-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{t('adminCredits:summary.actualCost')}</span>
            </div>
            <p className="text-xl font-bold mt-1">{formatUSD(summary.totalCostUsd)}</p>
            <p className="text-xs text-muted-foreground mt-1">{formatHKD(summary.totalCostHkd)}</p>
          </div>
        </div>
      )}

      {/* Platform-wide Usage History */}
      <div>
        <h3 className="text-lg font-semibold mb-3">{t('adminCredits:allUsage.title')}</h3>
        <div className="border rounded-lg overflow-hidden">
          {allUsageLoading ? (
            <div className="p-4 space-y-2">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 rounded" />)}
            </div>
          ) : allUsageRecords.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">{t('adminCredits:allUsage.empty')}</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('adminCredits:table.date')}</TableHead>
                      <TableHead>{t('adminCredits:table.user')}</TableHead>
                      <TableHead>{t('adminCredits:table.model')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.input')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.output')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.cacheRw')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.credits')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.costUsd')}</TableHead>
                      <TableHead className="text-right">{t('adminCredits:table.costHkd')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsageRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="text-xs">{formatDateTime(record.createdAt)}</TableCell>
                        <TableCell className="text-xs">{record.user?.name || record.user?.email || t('adminCredits:table.none')}</TableCell>
                        <TableCell className="text-xs">{record.model}</TableCell>
                        <TableCell className="text-right text-xs">{record.inputTokens.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-xs">{record.outputTokens.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground">
                          {(record.cacheReadTokens ?? 0).toLocaleString()}/{(record.cacheCreationTokens ?? 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-xs">{record.creditsDeducted.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-xs font-medium">{formatUSD(Number(record.costUsd))}</TableCell>
                        <TableCell className="text-right text-xs font-medium">{formatHKD(Number(record.costHkd))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {allUsageTotalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-3 border-t">
                  <Button variant="outline" size="sm" onClick={() => setAllUsagePage(p => Math.max(1, p - 1))} disabled={allUsagePage <= 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground">{t('adminCredits:pagination.pageOf', { page: allUsagePage, totalPages: allUsageTotalPages })}</span>
                  <Button variant="outline" size="sm" onClick={() => setAllUsagePage(p => Math.min(allUsageTotalPages, p + 1))} disabled={allUsagePage >= allUsageTotalPages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Per-User Credit Table */}
      <div>
        <h3 className="text-lg font-semibold mb-3">{t('adminCredits:users.title')}</h3>
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminCredits:table.user')}</TableHead>
                  <TableHead className="text-right">{t('adminCredits:users.balance')}</TableHead>
                  <TableHead className="text-right">{t('adminCredits:users.granted')}</TableHead>
                  <TableHead className="text-right">{t('adminCredits:users.used')}</TableHead>
                  <TableHead className="text-right">{t('adminCredits:users.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {user.avatar && (
                          <img src={user.avatar} alt="" className="h-6 w-6 rounded-full" />
                        )}
                        <div>
                          <div className="text-sm font-medium">{user.name || t('common:noName')}</div>
                          <div className="text-xs text-muted-foreground">{user.email}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={user.creditBalance <= 0 ? 'text-destructive font-medium' : ''}>
                        {user.creditBalance.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-sm">{Number(user.totalCreditsGranted).toLocaleString()}</TableCell>
                    <TableCell className="text-right text-sm">{Number(user.totalCreditsUsed).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => {
                            setSelectedUser(user);
                            setCreditsAmount(0);
                            setCreditsOperation('add');
                            setShowCreditsModal(true);
                          }}
                        >
                          {t('adminCredits:users.credits')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => openUsageModal(user)}
                        >
                          {t('adminCredits:users.history')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Credits Modal */}
      <Dialog open={showCreditsModal} onOpenChange={setShowCreditsModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('adminCredits:creditsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('adminCredits:creditsDialog.description', { user: selectedUser?.name || selectedUser?.email || '' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted rounded-lg p-4 space-y-1">
              <p className="text-sm"><span className="text-muted-foreground">{t('adminCredits:creditsDialog.currentBalance')}</span> <span className="font-medium">{selectedUser?.creditBalance.toLocaleString()}</span></p>
              <p className="text-sm"><span className="text-muted-foreground">{t('adminCredits:creditsDialog.totalGranted')}</span> {Number(selectedUser?.totalCreditsGranted).toLocaleString()}</p>
              <p className="text-sm"><span className="text-muted-foreground">{t('adminCredits:creditsDialog.totalUsed')}</span> {Number(selectedUser?.totalCreditsUsed).toLocaleString()}</p>
            </div>
            <div className="space-y-2">
              <Label>{t('adminCredits:creditsDialog.operation')}</Label>
              <div className="flex gap-2">
                <Button
                  variant={creditsOperation === 'add' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCreditsOperation('add')}
                >
                  {t('adminCredits:creditsDialog.addCredits')}
                </Button>
                <Button
                  variant={creditsOperation === 'set' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCreditsOperation('set')}
                >
                  {t('adminCredits:creditsDialog.setBalance')}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="creditsAmount">
                {creditsOperation === 'add' ? t('adminCredits:creditsDialog.creditsToAdd') : t('adminCredits:creditsDialog.newBalance')}
              </Label>
              <Input
                id="creditsAmount"
                type="number"
                min="0"
                step="0.01"
                value={creditsAmount}
                onChange={(e) => setCreditsAmount(parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreditsModal(false); setSelectedUser(null); }}>
              {t('common:cancel')}
            </Button>
            <Button onClick={updateCredits}>{t('adminCredits:creditsDialog.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Usage History Modal */}
      <Dialog open={showUsageModal} onOpenChange={(open) => { setShowUsageModal(open); if (!open) setSelectedUser(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('adminCredits:usageDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('adminCredits:usageDialog.description', { user: selectedUser?.name || selectedUser?.email || '' })}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {usageLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 rounded" />)}
              </div>
            ) : usageRecords.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">{t('adminCredits:usageDialog.empty')}</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('adminCredits:table.date')}</TableHead>
                        <TableHead>{t('adminCredits:table.model')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.input')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.output')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.cacheRw')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.credits')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.costUsd')}</TableHead>
                        <TableHead className="text-right">{t('adminCredits:table.costHkd')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usageRecords.map((record) => (
                        <TableRow key={record.id}>
                          <TableCell className="text-xs">{formatDateTime(record.createdAt)}</TableCell>
                          <TableCell className="text-xs">{record.model}</TableCell>
                          <TableCell className="text-right text-xs">{record.inputTokens.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-xs">{record.outputTokens.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">
                            {(record.cacheReadTokens ?? 0).toLocaleString()}/{(record.cacheCreationTokens ?? 0).toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right text-xs">{record.creditsDeducted.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-xs font-medium">{formatUSD(Number(record.costUsd))}</TableCell>
                          <TableCell className="text-right text-xs font-medium">{formatHKD(Number(record.costHkd))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {usageTotalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => setUsagePage(p => Math.max(1, p - 1))} disabled={usagePage <= 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">{t('adminCredits:pagination.pageOf', { page: usagePage, totalPages: usageTotalPages })}</span>
                    <Button variant="outline" size="sm" onClick={() => setUsagePage(p => Math.min(usageTotalPages, p + 1))} disabled={usagePage >= usageTotalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
